import type { ReactNode } from "react";
import { fmtPct, fmtValue } from "@/lib/format";

// --------------------------------------------------------------------------- //
// Source identity
// --------------------------------------------------------------------------- //

export const SOURCE_META: Record<string, { label: string; color: string }> = {
  repairs: { label: "Repairs", color: "text-amber-300 bg-amber-500/10 ring-amber-500/30" },
  odoo: { label: "Odoo", color: "text-violet-300 bg-violet-500/10 ring-violet-500/30" },
  prestashop: { label: "PrestaShop", color: "text-sky-300 bg-sky-500/10 ring-sky-500/30" },
  payments: { label: "Payments", color: "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30" },
  chat: { label: "IronChat", color: "text-cyan-300 bg-cyan-500/10 ring-cyan-500/30" },
  rota: { label: "IronRota", color: "text-fuchsia-300 bg-fuchsia-500/10 ring-fuchsia-500/30" },
  pim: { label: "PIM", color: "text-orange-300 bg-orange-500/10 ring-orange-500/30" },
};

export function SourceBadge({ code }: { code: string | null | undefined }) {
  if (!code) return null;
  const m = SOURCE_META[code] ?? { label: code, color: "text-slate-300 bg-slate-500/10 ring-slate-500/30" };
  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${m.color}`}>
      {m.label}
    </span>
  );
}

// --------------------------------------------------------------------------- //
// Status pills
// --------------------------------------------------------------------------- //

const STATUS_META: Record<string, { label: string; cls: string; dot: string }> = {
  ok: { label: "Healthy", cls: "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30", dot: "bg-emerald-400" },
  degraded: { label: "Degraded", cls: "text-amber-300 bg-amber-500/10 ring-amber-500/30", dot: "bg-amber-400" },
  stale: { label: "Stale", cls: "text-amber-300 bg-amber-500/10 ring-amber-500/30", dot: "bg-amber-400" },
  down: { label: "Down", cls: "text-rose-300 bg-rose-500/10 ring-rose-500/30", dot: "bg-rose-400" },
  no_data: { label: "No data", cls: "text-slate-300 bg-slate-500/10 ring-slate-500/30", dot: "bg-slate-400" },
  not_configured: { label: "Not configured", cls: "text-slate-400 bg-slate-500/10 ring-slate-500/20", dot: "bg-slate-500" },
  critical: { label: "Critical", cls: "text-rose-300 bg-rose-500/10 ring-rose-500/30", dot: "bg-rose-400" },
  warning: { label: "Warning", cls: "text-amber-300 bg-amber-500/10 ring-amber-500/30", dot: "bg-amber-400" },
  info: { label: "Info", cls: "text-sky-300 bg-sky-500/10 ring-sky-500/30", dot: "bg-sky-400" },
};

export function StatusPill({ status, label }: { status: string; label?: string }) {
  const m = STATUS_META[status] ?? { label: status, cls: "text-slate-300 bg-slate-500/10 ring-slate-500/30", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${m.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot} animate-pulse`} />
      {label ?? m.label}
    </span>
  );
}

// --------------------------------------------------------------------------- //
// Risk band pill (target pacing). Symbol + label so it never relies on colour.
// --------------------------------------------------------------------------- //

const BAND_META: Record<string, { label: string; cls: string; sym: string }> = {
  on_track: { label: "On track", cls: "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30", sym: "✓" },
  watch: { label: "Watch", cls: "text-amber-300 bg-amber-500/10 ring-amber-500/30", sym: "▲" },
  at_risk: { label: "At risk", cls: "text-orange-300 bg-orange-500/10 ring-orange-500/30", sym: "▲▲" },
  critical: { label: "Critical", cls: "text-rose-300 bg-rose-500/10 ring-rose-500/30", sym: "■" },
  no_target: { label: "No target", cls: "text-slate-400 bg-slate-500/10 ring-slate-500/20", sym: "—" },
  no_baseline: { label: "No baseline", cls: "text-slate-400 bg-slate-500/10 ring-slate-500/20", sym: "—" },
};

const TRUST_META: Record<string, { label: string; cls: string; sym: string }> = {
  trusted: { label: "Trusted", cls: "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30", sym: "✓" },
  partial: { label: "Partial", cls: "text-amber-300 bg-amber-500/10 ring-amber-500/30", sym: "◐" },
  estimated: { label: "Estimated", cls: "text-sky-300 bg-sky-500/10 ring-sky-500/30", sym: "≈" },
  stale: { label: "Stale", cls: "text-amber-300 bg-amber-500/10 ring-amber-500/30", sym: "⏳" },
  conflicting: { label: "Conflicting", cls: "text-rose-300 bg-rose-500/10 ring-rose-500/30", sym: "✗" },
  insufficient_data: { label: "Insufficient data", cls: "text-slate-400 bg-slate-500/10 ring-slate-500/20", sym: "∅" },
  unknown: { label: "Unknown", cls: "text-slate-400 bg-slate-500/10 ring-slate-500/20", sym: "?" },
};

export function TrustPill({ label }: { label: string }) {
  const m = TRUST_META[label] ?? { label, cls: "text-slate-300 bg-slate-500/10 ring-slate-500/30", sym: "•" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${m.cls}`}>
      <span aria-hidden>{m.sym}</span>{m.label}
    </span>
  );
}

const RECON_LABEL: Record<string, string> = {
  matched: "Matched", unmatched: "Unmatched", possible_match: "Possible match",
  conflict: "Conflict", not_applicable: "N/A", insufficient_data: "Insufficient data",
};

export function ReconStatusPill({ status }: { status: string }) {
  const sym = status === "matched" ? "✓" : status === "conflict" ? "✗" : status === "possible_match" ? "≈" : status === "insufficient_data" ? "∅" : "—";
  const cls = status === "matched" ? "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30"
    : status === "conflict" ? "text-rose-300 bg-rose-500/10 ring-rose-500/30"
    : status === "possible_match" ? "text-amber-300 bg-amber-500/10 ring-amber-500/30"
    : "text-slate-400 bg-slate-500/10 ring-slate-500/20";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${cls}`}>
      <span aria-hidden>{sym}</span>{RECON_LABEL[status] ?? status}
    </span>
  );
}

export function RiskBandPill({ band }: { band: string }) {
  const m = BAND_META[band] ?? { label: band, cls: "text-slate-300 bg-slate-500/10 ring-slate-500/30", sym: "•" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${m.cls}`}>
      <span aria-hidden>{m.sym}</span>{m.label}
    </span>
  );
}

// --------------------------------------------------------------------------- //
// Panels & headers
// --------------------------------------------------------------------------- //

export function Panel({
  title,
  subtitle,
  right,
  children,
  className = "",
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel ${className}`}>
      {(title || right) && (
        <header className="flex items-start justify-between gap-3 border-b border-ink-500/60 px-5 py-3.5">
          <div>
            {title && <h2 className="text-sm font-semibold text-slate-100">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>}
          </div>
          {right}
        </header>
      )}
      <div className="panel-pad">{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  description,
  right,
}: {
  title: string;
  description?: string;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-white">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-slate-400">{description}</p>}
      </div>
      {right}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Deltas
// --------------------------------------------------------------------------- //

export function DeltaBadge({
  pct,
  invert = false,
}: {
  pct: number | null | undefined;
  invert?: boolean;
}) {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-500">—</span>;
  const up = pct >= 0;
  const good = invert ? !up : up;
  const cls = pct === 0 ? "text-slate-400" : good ? "text-emerald-400" : "text-rose-400";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${cls}`}>
      {pct === 0 ? "→" : up ? "▲" : "▼"} {fmtPct(pct)}
    </span>
  );
}

// --------------------------------------------------------------------------- //
// KPI card
// --------------------------------------------------------------------------- //

export function KpiCard({
  label,
  value,
  unit,
  delta,
  source,
  tone,
  invertDelta,
}: {
  label: string;
  value: number | null | undefined;
  unit?: string;
  delta?: { pct: number | null } | null;
  source?: string;
  tone?: "danger";
  invertDelta?: boolean;
}) {
  const danger = tone === "danger" && typeof value === "number" && value > 0;
  return (
    <div className={`panel panel-pad relative overflow-hidden ${danger ? "ring-1 ring-rose-500/40" : ""}`}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
        {source && <SourceBadge code={source} />}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-2xl font-semibold tabular-nums ${danger ? "text-rose-300" : "text-white"}`}>
          {fmtValue(value ?? null, unit)}
        </span>
        {delta && <DeltaBadge pct={delta.pct} invert={invertDelta} />}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Bar list / leaderboard
// --------------------------------------------------------------------------- //

export function BarList({
  rows,
  unit,
  empty = "No data in range.",
}: {
  rows: { label: string; value: number }[];
  unit?: string;
  empty?: string;
}) {
  if (!rows.length) return <EmptyState compact>{empty}</EmptyState>;
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <ul className="space-y-2.5">
      {rows.map((r, i) => (
        <li key={`${r.label}-${i}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="truncate text-slate-300">{r.label}</span>
            <span className="tabular-nums text-slate-400">{fmtValue(r.value, unit)}</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ink-600">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400"
              style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function Leaderboard({
  rows,
  unit,
}: {
  rows: { name: string; value: number; count?: number; orders?: number }[];
  unit?: string;
}) {
  if (!rows.length) return <EmptyState compact>No entries in range.</EmptyState>;
  return (
    <ol className="space-y-1">
      {rows.map((r, i) => (
        <li key={`${r.name}-${i}`} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-ink-700/60">
          <span className="w-5 text-center text-xs font-semibold text-slate-500">{i + 1}</span>
          <span className="flex-1 truncate text-sm text-slate-200">{r.name || "—"}</span>
          {(r.count ?? r.orders) !== undefined && (
            <span className="text-[11px] text-slate-500">{r.count ?? r.orders}×</span>
          )}
          <span className="tabular-nums text-sm font-medium text-white">{fmtValue(r.value, unit)}</span>
        </li>
      ))}
    </ol>
  );
}

// --------------------------------------------------------------------------- //
// State helpers
// --------------------------------------------------------------------------- //

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-600 border-t-brand-400" />
      {label}
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-sm text-rose-300">
      Failed to load: {msg}
    </div>
  );
}

export function EmptyState({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  return (
    <div className={`text-center text-sm text-slate-500 ${compact ? "py-4" : "py-10"}`}>{children}</div>
  );
}

export function GapList({ gaps }: { gaps: string[] }) {
  if (!gaps.length) return null;
  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-300">Data gaps</p>
      <ul className="mt-1.5 space-y-1 text-xs text-amber-200/80">
        {gaps.map((g, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-amber-500">•</span>
            {g}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-lg border border-ink-500/50 bg-ink-700/40 px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-white">{value}</p>
      {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}
